/**
 * Phase 5.9.12 (Feb 2026) — Smart relative timestamps for the order list.
 *
 * Replaces bare `toLocaleTimeString()` which silently dropped the date and made
 * all rows look like they happened today. Mirrors how Gmail / Slack render
 * conversation lists: compact for "today", increasingly explicit as items age.
 *
 *   Today        → "10:28 AM"
 *   Yesterday    → "Yesterday 10:28 AM"
 *   < 7 days     → "Mon 10:28 AM"
 *   This year    → "Feb 8 · 10:28 AM"
 *   Older        → "Feb 8 2025 · 10:28 AM"
 *
 * Pure function, locale-aware, no deps.
 */
export function formatOrderTimestamp(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const now = new Date();
  const time = d.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });

  const sameDay = d.toDateString() === now.toDateString();
  if (sameDay) return time;

  const yesterday = new Date(now);
  yesterday.setDate(yesterday.getDate() - 1);
  if (d.toDateString() === yesterday.toDateString()) return `Yesterday ${time}`;

  const diffDays = (now - d) / (1000 * 60 * 60 * 24);
  if (diffDays >= 0 && diffDays < 7) {
    const weekday = d.toLocaleDateString([], { weekday: 'short' });
    return `${weekday} ${time}`;
  }

  if (d.getFullYear() === now.getFullYear()) {
    const md = d.toLocaleDateString([], { month: 'short', day: 'numeric' });
    return `${md} · ${time}`;
  }

  const mdy = d.toLocaleDateString([], { month: 'short', day: 'numeric', year: 'numeric' });
  return `${mdy} · ${time}`;
}
